import { Component } from 'preact'
import Axios from 'axios'
import classnames from 'classnames'

import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'
import formatDateInLocalTimezone from '@insided/shared/utils/dateFormatting'
import { Reply } from '../../interfaces'
import { ContentType } from '@insided/shared/utils/contentType'
import { REPLY } from '../../constants'

import PostAuthor from '../PostAuthor'
import PostMetadata from '../PostMetadata'
import ReplyContent from './ReplyContent'
import ReplyContentActions from './ReplyContentActions'
import ReplyActions from './ReplyActions'
import ToggleTrashReplyModal from '../ToggleTrashReplyModal'

/* eslint-disable import/no-internal-modules */
import tracker from '@insided/deprecated-components/tracker/publish'
import UserProfileActions from '../UserProfileActions'
import PostAuthorSignature from '../PostAuthorSignature'
import {
  pinReply,
  toggleReplyTrashed,
  unpinReply
} from '@insided/control/modules/ContentModeration/TopicModeration/services/topicService'

interface ReplyPostProps extends Reply {
  contentType: ContentType
  content: string
  isAuthor?: boolean
  isPinned?: boolean
  replyNumber?: number
  replyCount?: number
  topicId: number
  topicPublicId: string
  topicTitle: string
  onTogglePin: (pinnedReplyId: string | null) => void
  onToggleSpam: (replyId?: string) => void
  onSelectReply: (selectedReplyId: string) => void
  destinationUrl: string
  selectedReplies: string[]
  onShowAlert: (type: 'success' | 'error', message?: string | undefined) => void
  onToggleTrash: (replyId: string) => void
  isProductboardNoteCreationEnabled: boolean
  isZendeskTicketEscalationEnabled: boolean
  isSalesforceCaseEscalationEnabled: boolean
  isFreshdeskTicketEscalationEnabled: boolean
  getReplies: (page?: number, pageSize?: number, sort?: string) => Promise<any>
}

interface State extends Reply {
  contentType: ContentType
  isAuthor?: boolean
  isPinned?: boolean
  replyNumber?: number
  replyCount?: number
  pendingApproval: boolean
  showReplyEditor: boolean
  showToggleTrashReplyModal: boolean
}

class ReplyPost extends Component<ReplyPostProps, State> {
  private readonly userId: string = getInsidedData('user').userid

  constructor(props: ReplyPostProps) {
    super(props)
    const likedByOpener = this.props.likedBy.includes(getInsidedData('user').userid)
    this.state = {
      ...props,
      showReplyEditor: false,
      likedByOpener: likedByOpener,
      showToggleTrashReplyModal: false
    }
    this.handleTrash = this.handleTrash.bind(this)
    this.onChangeContent = this.onChangeContent.bind(this)
    this.onShowEditor = this.onShowEditor.bind(this)
    this.onSaveEditor = this.onSaveEditor.bind(this)
    this.onCancelEditor = this.onCancelEditor.bind(this)
    this.onApprove = this.onApprove.bind(this)
    this.showToggleTrashReplyModal = this.showToggleTrashReplyModal.bind(this)
  }

  async handleTrash() {
    const setTrashed = async (trashed: boolean) => {
      const { contentType, onToggleTrash } = this.props
      const { id } = this.state
      try {
        await toggleReplyTrashed(contentType, Number(id), trashed)
        this.setState({ trashed })
        onToggleTrash(id)
        if (this.state.isPinned) {
          this.togglePin()
        }
      } catch {}
    }

    await setTrashed(!this.props.trashed)
    await this.props.getReplies()
    this.props.onShowAlert(
      'success',
      this.props.trashed ? REPLY.TRASHED_SUCCESS_MESSAGE : REPLY.RESTORED_SUCCESS_MESSAGE
    )
  }

  handleTogglePin = async () => {
    const { id } = this.state
    const { contentType, isPinned } = this.props
    try {
      if (isPinned) {
        await unpinReply(contentType, Number(id))
      } else {
        await pinReply(contentType, Number(id))
      }
      this.togglePin()
    } catch {}
  }

  togglePin = () => {
    const { id } = this.state
    const { topicPublicId, contentType, isPinned } = this.props

    this.setState({ isPinned: !this.state.isPinned })
    this.props.onTogglePin(isPinned ? null : id)
    tracker.trackEvent('userevent', {
      type: `Reply ${isPinned ? 'Unpinned' : 'Pinned'}`,
      reply_id: id,
      topic_content_type: contentType,
      topic_id: topicPublicId
    })
  }

  handleToggleLike = async () => {
    const { id } = this.state
    const action = this.state.likedByOpener ? 'unlike' : 'like'
    await Axios.post(`/community/api/${this.props.contentType}s/replies/${id}/${action}`).then(() => {
      const userId = getInsidedData('user').userid
      if (this.state.likedByOpener) {
        this.setState({ likedBy: this.state.likedBy.filter((e) => e !== userId), likedByOpener: false })
      } else {
        const likedBy = this.state.likedBy
        likedBy.push(userId)
        this.setState({ likedBy: likedBy, likedByOpener: true })
      }
    })
  }

  onChangeContent(content: string) {
    this.setState({ content })
  }

  onShowEditor() {
    this.setState({ showReplyEditor: true })
  }

  onCancelEditor() {
    this.setState({ showReplyEditor: false })
  }

  onSaveEditor(content: string) {
    this.setState({ showReplyEditor: false, content })
  }

  onApprove() {
    this.setState({ pendingApproval: false })
  }

  showToggleTrashReplyModal(show: boolean) {
    this.setState({ showToggleTrashReplyModal: show })
  }

  render() {
    const {
      id,
      author,
      trashed,
      repliedAt,
      replyNumber,
      replyCount,
      contentType,
      topicId,
      isPinned = false,
      onToggleSpam,
      onSelectReply,
      selectedReplies,
      spam,
      onShowAlert,
      isProductboardNoteCreationEnabled,
      topicTitle,
      isZendeskTicketEscalationEnabled,
      isFreshdeskTicketEscalationEnabled,
      destinationUrl,
      isSalesforceCaseEscalationEnabled,
      getReplies
    } = this.props
    const { content, pendingApproval, showReplyEditor, showToggleTrashReplyModal } = this.state

    const publishedAt = formatDateInLocalTimezone(new Date(repliedAt).getTime() / 1000)
    const selected = selectedReplies.includes(id)

    return (
      <article
        id={`post-${id}`}
        data-post-id={id}
        className={classnames('post', 'panel', 'panel-default', {
          'status--trashed': trashed,
          'status--pin': isPinned,
          'status--spam': spam,
          'status--selected': selected
        })}
      >
        <div className='post--inner-wrapper'>
          <div className='panel-body'>
            <PostAuthor author={author} />
            <div className='post--content-container'>
              <PostMetadata
                publishedAt={publishedAt}
                spam={spam}
                pending={pendingApproval}
                trashed={trashed}
                pinned={isPinned}
                replyNumber={replyNumber}
                replyCount={replyCount}
              />
              <ReplyContent
                id={id}
                content={content}
                contentType={contentType}
                topicId={topicId}
                showEditor={showReplyEditor}
                onSave={this.onSaveEditor}
                onCancel={this.onCancelEditor}
                onShowAlert={onShowAlert}
              />
              {author.signature && <PostAuthorSignature signature={author.signature} />}
              {!spam && (
                <ReplyContentActions
                  author={author}
                  content={content}
                  topicId={topicId}
                  id={id}
                  contentType={contentType}
                  isPinned={isPinned}
                  trashed={trashed}
                  likedByOpener={this.state.likedByOpener}
                  likedBy={this.state.likedBy}
                  handleTogglePin={this.handleTogglePin}
                  handleToggleLike={this.handleToggleLike}
                  isProductboardNoteCreationEnabled={isProductboardNoteCreationEnabled}
                  isSalesforceCaseEscalationEnabled={isSalesforceCaseEscalationEnabled}
                  isZendeskTicketEscalationEnabled={isZendeskTicketEscalationEnabled}
                  isFreshdeskTicketEscalationEnabled={isFreshdeskTicketEscalationEnabled}
                />
              )}
            </div>
          </div>
          <div className='post__panel-footer clearfix'>
            <UserProfileActions authorId={author.id} loggedInUserId={this.userId} />
            <ReplyActions
              trashed={trashed}
              spam={spam}
              id={id}
              topicTitle={topicTitle}
              author={author}
              isZendeskTicketEscalationEnabled={isZendeskTicketEscalationEnabled}
              isProductboardNoteCreationEnabled={isProductboardNoteCreationEnabled}
              isSalesforceCaseEscalationEnabled={isSalesforceCaseEscalationEnabled}
              isFreshdeskTicketEscalationEnabled={isFreshdeskTicketEscalationEnabled}
              title={topicTitle}
              content={content}
              contentType={contentType}
              isReply
              destinationUrl={destinationUrl}
              topicId={topicId}
              moderatorId={this.userId}
              onToggleSpam={onToggleSpam}
              onToggleTrash={this.showToggleTrashReplyModal}
              handleTrash={this.handleTrash}
              pending={pendingApproval}
              onApprove={this.onApprove}
              onShowAlert={onShowAlert}
              onShowEditor={this.onShowEditor}
              onSelectReply={onSelectReply}
              selected={selected}
              getReplies={getReplies}
            />
          </div>
        </div>
        {showToggleTrashReplyModal && (
          <ToggleTrashReplyModal
            contentType={contentType}
            topicId={topicId}
            trashed={trashed}
            handleTrash={this.handleTrash}
            onClose={() => this.showToggleTrashReplyModal(false)}
            onShowAlert={onShowAlert}
          />
        )}
      </article>
    )
  }
}

export default ReplyPost
